// Writing a Saju book's manuscripts again (specs/011 §5): the scopes a document's narrative components hold, what a write
// must satisfy before it lands, and the text each instance keeps after it. Pure: the Studio (workspace.mjs) brings the
// facts of the person now, the print metrics and the local wiki's answer about the cited paths.
import { SUBJECT_KEYS } from './component-saju.mjs';
import { NARRATIVE_COMPONENT, describeNarrative, narrativeIssues } from './component-saju-narrative.mjs';
import { bookTextOf } from './book-text.mjs';
import { styleIssues } from './narrative-style.mjs';

// What the server refuses in a component's strings (StudioDocumentExtensions.kt componentResource): said before a save.
const RESOURCE = /(?:javascript:|data:|https?:|url\s*\()/i;
// A path into a scope's facts: dotted names (the facts name some in Korean: natal.tenGods.비견) and [indexes] —
// daeun.ten_god_stem, natal.pillars[2].stem.
export const FACT_PATH = /^[\p{L}_][\p{L}\p{N}_]*(?:\.[\p{L}_][\p{L}\p{N}_]*|\[\d{1,3}\])*$/u;
// The book whose manuscripts are read with the wealth reports' money rules (narrative-style.mjs).
const MONEY_PRODUCTS = new Set(['wealth-flow']);

const scopeKey = scope => JSON.stringify(scope);
const personOf = props => JSON.stringify(SUBJECT_KEYS.map(key => props[key] ?? null));
const usedSlots = component => new Set(component.props.boxes.flatMap(box => box.segments.filter(segment => segment.text).map(segment => segment.text)));

/**
 * The manuscript scopes of a document, each with every instance of it: [{ scope, label, person (birth input), product,
 * instances: [{ page, section, component }], slots: { address: text }, factsHash (the facts now; null when instances
 * disagree), writtenFrom, review, basis?, citations?, generator? }]. A page's `book` component holds only figures and
 * is never written.
 */
export function narrativeScopes(content) {
  const groups = new Map();
  (content?.sections ?? []).forEach((section, index) => {
    for (const component of section.components ?? []) {
      if (component?.type !== NARRATIVE_COMPONENT.type || component.props?.scope?.series === 'book') continue;
      const key = `${personOf(component.props)}|${scopeKey(component.props.scope)}`;
      if (!groups.has(key)) groups.set(key, { scope: component.props.scope, label: describeNarrative(component.props),
        person: Object.fromEntries(SUBJECT_KEYS.map(name => [name, component.props[name]])), product: null, instances: [] });
      const group = groups.get(key);
      group.product ??= bookTextOf(section)?.product ?? null;
      group.instances.push({ page: index + 1, section, component });
    }
  });
  return [...groups.values()].map(group => {
    const texts = group.instances.map(instance => instance.component.props.text);
    const current = [...new Set(group.instances.map(instance => instance.component.snapshot.data.factsHash))];
    const writtenFrom = [...new Set(texts.map(text => text.factsHash))];
    const { basis, citations, generator } = texts[0];
    return { ...group, slots: Object.assign({}, ...texts.map(text => text.slots)), factsHash: current.length === 1 ? current[0] : null,
      writtenFrom: writtenFrom.length === 1 ? writtenFrom[0] : null, review: texts[0].review,
      ...(basis ? { basis } : {}), ...(citations ? { citations } : {}), ...(generator ? { generator } : {}) };
  });
}

/** A scope's instances by page: stale when the text was written from other facts than the person's now. */
export const scopeStale = group => group.factsHash === null || group.writtenFrom !== group.factsHash;

/** The value at a fact path ('daeun.ten_god_stem', 'natal.pillars[2].stem'), or undefined. */
export function factAt(facts, path) {
  if (typeof path !== 'string' || !FACT_PATH.test(path)) return undefined;
  let value = facts;
  for (const [, name, index] of path.matchAll(/([\p{L}_][\p{L}\p{N}_]*)|\[(\d+)\]/gu)) {
    if (value === null || typeof value !== 'object') return undefined;
    value = name !== undefined ? (Object.hasOwn(value, name) ? value[name] : undefined) : value[Number(index)];
  }
  return value;
}

/**
 * The props an instance keeps after a write: the slots its own boxes print (a scope's text spans pages that print
 * different slots), the Studio's hash of the facts now — never the writer's — and a draft to review; `record` is
 * { basis, citations, generator } (a person's edit keeps what the text was written with).
 */
export function writtenProps(component, text, factsHash, record) {
  const used = usedSlots(component);
  return { ...component.props, text: { slots: Object.fromEntries(Object.entries(text).filter(([address]) => used.has(address))), factsHash,
    review: { state: 'draft' }, ...Object.fromEntries(Object.entries(record).filter(([, value]) => value !== undefined)) } };
}

/**
 * Why a write into `group` is refused: [{ code, message }] — none when it may land. `input` is the writer's
 * { expectedFactsHash, text: { address: text }, basis, citations, generator }; `context` the Studio's: { facts (the
 * scope's facts now), missingWiki (the cited paths the local wiki does not have), person (a person's edit in the
 * review tab: no basis, citations or generator asked) }. The print metrics must be loaded (overflow).
 */
export function writeRefusals(group, input, { facts, missingWiki = [], person = false }) {
  const refusals = [], refuse = (code, message) => refusals.push({ code, message });
  const text = input?.text;
  if (!text || typeof text !== 'object' || Array.isArray(text)) return [{ code: 'TEXT', message: 'text 는 { 원고 칸 주소: 글 } 객체여야 합니다.' }];
  for (const [address, value] of Object.entries(text)) {
    if (typeof value !== 'string' || !value.trim() || value.length > 20000) refuse('TEXT', `${address}: 1~20,000자 글이어야 합니다.`);
    else if (RESOURCE.test(value)) refuse('RESOURCE', `${address}: 주소·실행 코드처럼 읽히는 글(http:, data:, javascript:, url()은 저장할 수 없습니다.`);
  }
  if (group.factsHash === null) refuse('FACTS', '같은 범위의 컴포넌트들이 서로 다른 사실로 계산되어 있습니다. 명식을 다시 적용해 주세요.');
  else if (input.expectedFactsHash !== group.factsHash) refuse('FACTS', `expectedFactsHash 가 지금 명식의 사실(${group.factsHash.slice(0, 12)}…)과 다릅니다 — list_narratives 로 다시 읽고 쓰세요.`);
  const wanted = Object.keys(group.slots).sort(), given = Object.keys(text).sort();
  if (wanted.join('\n') !== given.join('\n')) {
    const missing = wanted.filter(address => !given.includes(address)), extra = given.filter(address => !wanted.includes(address));
    refuse('SLOTS', `원고 칸이 범위의 칸과 다릅니다${missing.length ? ` — 빠짐: ${missing.join(', ')}` : ''}${extra.length ? ` — 없는 칸: ${extra.join(', ')}` : ''}.`);
  }
  const money = MONEY_PRODUCTS.has(group.product);
  for (const [address, value] of Object.entries(text)) if (typeof value === 'string') {
    for (const issue of styleIssues(value, { money })) refuse('STYLE', `${address}: ${issue.label} — 「${issue.sentence}」`);
  }
  if (!person) {
    const basis = input.basis;
    if (!Array.isArray(basis) || !basis.length || basis.length > 200) refuse('BASIS', 'basis 는 글이 근거로 삼은 사실 경로 1~200개입니다.');
    else for (const path of basis) if (factAt(facts, path) === undefined) refuse('BASIS', `basis ${JSON.stringify(path)} 가 이 범위의 사실에 없습니다(경로 예: daeun.ten_god_stem, natal.pillars[2].stem).`);
    const citations = input.citations;
    if (!Array.isArray(citations) || !citations.length || citations.length > 50 || citations.some(item => !item || typeof item.wiki !== 'string' || Object.keys(item).length !== 1)) {
      refuse('CITATIONS', 'citations 는 [{ wiki: \'guides/운-해석-workflow.md#…\' }] 1~50개입니다.');
    }
    for (const path of missingWiki) refuse('CITATIONS', `wiki 에 없는 문서·제목입니다: ${path}`);
    const generator = input.generator;
    if (!generator || typeof generator.agent !== 'string' || !generator.agent.trim() || typeof generator.guide !== 'string' || !generator.guide.trim()
      || Object.keys(generator).some(key => !['agent', 'guide'].includes(key))) refuse('GENERATOR', 'generator 는 { agent, guide } 입니다(쓴 도우미와 따른 지침).');
  }
  // Every box that prints a written slot, on every page, in the lines the book gives it — the check the outputs make.
  if (!refusals.some(refusal => ['TEXT', 'SLOTS', 'FACTS'].includes(refusal.code))) {
    for (const { page, component } of group.instances) {
      const props = writtenProps(component, text, group.factsHash, {});
      for (const issue of narrativeIssues(props, component.snapshot)) {
        if (issue.kind === 'overflow') refuse('OVERFLOW', `${page}쪽 ${issue.box}: ${issue.lines}줄이 되어 자리를 넘칩니다.`);
        if (issue.kind === 'unsupported') refuse('UNSUPPORTED', `${page}쪽 ${issue.box}: 책 글꼴에 없는 글자 ${issue.characters.join('')}`);
      }
    }
  }
  return refusals;
}
